/**
 * PDF Review Highlight Overlay
 *
 * Renders colored highlight rectangles over matched text on PDF pages.
 * Handles click/hover events and emits CustomEvents for bidirectional navigation.
 */

const STATUS_CLASSES = {
  matched: 'pdf-review-highlight--matched',
  multiple: 'pdf-review-highlight--multiple',
  unmatched: 'pdf-review-highlight--unmatched',
  reviewed: 'pdf-review-highlight--reviewed',
  flagged: 'pdf-review-highlight--flagged',
  found: 'pdf-review-highlight--multiple'
}

export class HighlightOverlay {
  constructor(viewerContainer, { scrollContainer, onHighlightClick, onHighlightHover, onHighlightAction, getFieldSummary } = {}) {
    this.viewerContainer = viewerContainer
    this.scrollContainer = scrollContainer || viewerContainer.closest('[data-pdf-review-target~="viewerContainer"]') || viewerContainer.parentElement
    this.overlays = new Map() // pageIndex → overlayDiv
    this.highlights = new Map() // fieldPath → [{el, pageIndex}]
    this.onHighlightClick = onHighlightClick
    this.onHighlightHover = onHighlightHover
    this.onHighlightAction = onHighlightAction
    this.getFieldSummary = getFieldSummary
    this.hidePopoverTimeout = null
  }

  /**
   * Ensure an overlay div exists for the given page element.
   */
  ensureOverlay(pageElement, pageIndex) {
    if (this.overlays.has(pageIndex)) return this.overlays.get(pageIndex)

    const overlay = document.createElement('div')
    overlay.className = 'pdf-review-overlay'
    overlay.dataset.pageIndex = pageIndex
    overlay.style.position = 'absolute'
    overlay.style.inset = '0'
    overlay.style.pointerEvents = 'none'
    overlay.style.zIndex = '5'

    pageElement.style.position = 'relative'
    pageElement.appendChild(overlay)
    this.overlays.set(pageIndex, overlay)
    return overlay
  }

  /**
   * Render highlights for match results.
   * Computes positions from live DOM rects (scroll-stable relative to page).
   *
   * @param {Map} matchResults - fieldPath → {status, matches, confidence}
   * @param {Set|Map} reviewedPaths - Set of paths that have been reviewed
   */
  render(matchResults, reviewedPaths = new Set()) {
    this.clear()

    for (const [fieldPath, result] of matchResults) {
      if (result.status === 'unmatched' || result.status === 'skipped') continue

      const reviewEntry = typeof reviewedPaths.get === 'function' ? reviewedPaths.get(fieldPath) : null
      const isReviewed = reviewEntry || reviewedPaths.has?.(fieldPath)
      const isFlagged = reviewEntry?.match_status === 'flagged'
      const statusClass = isFlagged ? STATUS_CLASSES.flagged : (isReviewed ? STATUS_CLASSES.reviewed : STATUS_CLASSES[result.status])

      result.matches.forEach((match, matchIdx) => {
        const pageElement = this.getPageElement(match.pageIndex)
        if (!pageElement) return

        const overlay = this.ensureOverlay(pageElement, match.pageIndex)
        const pageRect = pageElement.getBoundingClientRect()

        // Compute union bounding rect from live span positions (scroll-safe)
        const spanRects = match.spans.map(s => s.el.getBoundingClientRect())
        const unionRect = computeUnionRect(spanRects, pageRect)

        const highlightEl = document.createElement('div')
        highlightEl.className = `pdf-review-highlight ${statusClass}`
        highlightEl.dataset.fieldPath = fieldPath
        highlightEl.dataset.matchIndex = matchIdx
        if (result.confidence != null) {
          highlightEl.dataset.confidence = result.confidence.toFixed(2)
        }
        highlightEl.style.position = 'absolute'
        highlightEl.style.left = `${unionRect.left}px`
        highlightEl.style.top = `${unionRect.top}px`
        highlightEl.style.width = `${unionRect.width}px`
        highlightEl.style.height = `${unionRect.height}px`
        highlightEl.style.pointerEvents = 'auto'
        highlightEl.style.cursor = 'pointer'

        highlightEl.setAttribute('role', 'button')
        highlightEl.setAttribute('aria-label', `Matched field: ${fieldPath}`)
        highlightEl.setAttribute('tabindex', '0')

        highlightEl.addEventListener('click', (e) => {
          e.stopPropagation()
          this.onHighlightClick?.(fieldPath, match)
        })

        highlightEl.addEventListener('mouseenter', () => {
          this.showPopover(fieldPath, match, highlightEl, unionRect, overlay)
          this.onHighlightHover?.(fieldPath, match, true)
        })

        highlightEl.addEventListener('mouseleave', () => {
          this.scheduleHidePopover()
          this.onHighlightHover?.(fieldPath, match, false)
        })

        highlightEl.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.onHighlightClick?.(fieldPath, match)
          }
        })

        overlay.appendChild(highlightEl)

        // Add persistent badge if already reviewed
        if (isReviewed) {
          this._updateStatusBadge(highlightEl, isFlagged ? 'flagged' : 'reviewed')
        }

        // Track for later lookup
        if (!this.highlights.has(fieldPath)) {
          this.highlights.set(fieldPath, [])
        }
        this.highlights.get(fieldPath).push({ el: highlightEl, pageIndex: match.pageIndex })
      })
    }
    this.setCurrent(this.currentPath)
  }

  /**
   * The field being checked keeps a strong outline and every other one fades,
   * so a page with a hundred values still points at one. Survives re-renders.
   */
  setCurrent(fieldPath) {
    this.currentPath = fieldPath || null
    this.viewerContainer.querySelectorAll('.pdf-review-highlight--current')
      .forEach(el => el.classList.remove('pdf-review-highlight--current'))
    const entries = fieldPath ? this.highlights.get(fieldPath) : null
    entries?.forEach(({ el }) => el.classList.add('pdf-review-highlight--current'))
    this.overlays.forEach(overlay => overlay.classList.toggle('pdf-review-overlay--focusing', Boolean(entries?.length)))
  }

  /**
   * Flash a highlight for a given field path (used for bidirectional navigation).
   */
  flashHighlight(fieldPath) {
    const entries = this.highlights.get(fieldPath)
    if (!entries || entries.length === 0) return null

    const first = entries[0]
    first.el.classList.add('pdf-review-highlight--pulse')
    setTimeout(() => {
      first.el.classList.remove('pdf-review-highlight--pulse')
    }, 1500)

    return first
  }

  previewHighlight(fieldPath) {
    const entries = this.highlights.get(fieldPath)
    if (!entries || entries.length === 0) return

    entries.forEach(({ el }) => el.classList.add('pdf-review-highlight--preview'))
  }

  clearPreview(fieldPath) {
    const entries = this.highlights.get(fieldPath)
    if (!entries) return

    entries.forEach(({ el }) => el.classList.remove('pdf-review-highlight--preview'))
  }

  pulseAllHighlights(limit = 12) {
    let count = 0
    for (const entries of this.highlights.values()) {
      const first = entries[0]
      if (!first) continue

      first.el.classList.add('pdf-review-highlight--pulse')
      setTimeout(() => first.el.classList.remove('pdf-review-highlight--pulse'), 1500)

      count += 1
      if (count >= limit) break
    }
  }

  /**
   * Scroll the viewer to show the highlight for a field path.
   */
  scrollToHighlight(fieldPath) {
    const entry = this.flashHighlight(fieldPath)
    if (!entry) return

    this.scrollElementIntoPdfPane(entry.el)
  }

  scrollElementIntoPdfPane(element) {
    const scroller = this.scrollContainer
    if (!scroller) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      return
    }

    const elementRect = element.getBoundingClientRect()
    const scrollerRect = scroller.getBoundingClientRect()
    const targetTop = scroller.scrollTop +
      (elementRect.top - scrollerRect.top) -
      (scroller.clientHeight / 2) +
      (elementRect.height / 2)

    scroller.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth'
    })
  }

  /**
   * Update highlight status (e.g., when a field is reviewed).
   */
  updateHighlightStatus(fieldPath, status) {
    const entries = this.highlights.get(fieldPath)
    if (!entries) return

    entries.forEach(({ el }) => {
      Object.values(STATUS_CLASSES).forEach(cls => el.classList.remove(cls))
      el.classList.add(STATUS_CLASSES[status] || STATUS_CLASSES.matched)

      // Add/update the persistent status badge
      this._updateStatusBadge(el, status)

      // Play a confirmation animation on approve/flag
      if (status === 'reviewed' || status === 'flagged') {
        el.classList.add('pdf-review-highlight--confirmed')
        setTimeout(() => el.classList.remove('pdf-review-highlight--confirmed'), 600)
      }
    })
  }

  /**
   * Render a small persistent badge (✓ or ⚑) in the corner of the highlight
   * so you can visually scan which fields are confirmed vs pending.
   */
  _updateStatusBadge(highlightEl, status) {
    let badge = highlightEl.querySelector('.pdf-review-badge')
    if (status === 'reviewed') {
      if (!badge) {
        badge = document.createElement('span')
        badge.className = 'pdf-review-badge pdf-review-badge--ok'
        highlightEl.appendChild(badge)
      }
      badge.className = 'pdf-review-badge pdf-review-badge--ok'
      badge.textContent = '✓'
    } else if (status === 'flagged') {
      if (!badge) {
        badge = document.createElement('span')
        badge.className = 'pdf-review-badge pdf-review-badge--flag'
        highlightEl.appendChild(badge)
      }
      badge.className = 'pdf-review-badge pdf-review-badge--flag'
      badge.textContent = '⚑'
    } else {
      // Remove badge if going back to unreviewed
      badge?.remove()
      return
    }

    this._placeStatusBadge(highlightEl, badge)
  }

  _placeStatusBadge(highlightEl, badge) {
    const overlayWidth = highlightEl.parentElement?.clientWidth || 0
    const highlightLeft = parseFloat(highlightEl.style.left) || 0
    const highlightWidth = parseFloat(highlightEl.style.width) || 0
    const badgeSpace = 16
    const shouldPlaceLeft = overlayWidth > 0 && highlightLeft + highlightWidth + badgeSpace > overlayWidth

    badge.classList.toggle('pdf-review-badge--left', shouldPlaceLeft)
    badge.classList.toggle('pdf-review-badge--right', !shouldPlaceLeft)
  }

  showPopover(fieldPath, match, highlightEl, rect, overlay) {
    clearTimeout(this.hidePopoverTimeout)
    this.popover?.remove()

    const popover = document.createElement('div')
    const summary = this.getFieldSummary?.(fieldPath) || { label: fieldPath, value: '' }
    popover.className = 'pdf-review-popover'
    popover.dataset.fieldPath = fieldPath

    const truncatedLabel = summary.label.length > 22 ? summary.label.slice(0, 20) + '…' : summary.label
    const truncatedValue = summary.value.length > 16 ? summary.value.slice(0, 14) + '…' : summary.value

    popover.innerHTML = `
      <span class="pdf-review-popover-label" title="${escapeHtml(summary.fullLabel || summary.label)}">${escapeHtml(truncatedLabel)}</span>
      <span class="pdf-review-popover-sep">→</span>
      <span class="pdf-review-popover-val" title="${escapeHtml(summary.value)}">${escapeHtml(truncatedValue)}</span>
      <button type="button" class="pdf-review-popover-btn pdf-review-popover-btn--ok" data-action="approve" title="Approve"><i class="ti ti-check"></i></button>
      <button type="button" class="pdf-review-popover-btn pdf-review-popover-btn--flag" data-action="flag" title="Flag"><i class="ti ti-flag-filled"></i></button>
    `

    const overlayRect = overlay.getBoundingClientRect()
    const highlightRect = highlightEl.getBoundingClientRect()
    const placeBelow = highlightRect.top - overlayRect.top < 48
    const top = placeBelow
      ? Math.min(overlayRect.height - 36, highlightRect.bottom - overlayRect.top + 6)
      : Math.max(4, highlightRect.top - overlayRect.top - 38)

    // Center popover on the highlight, clamped to stay within the overlay
    const highlightCenter = highlightRect.left - overlayRect.left + highlightRect.width / 2
    const popoverWidth = 260
    const left = Math.max(4, Math.min(highlightCenter - popoverWidth / 2, overlayRect.width - popoverWidth - 4))
    popover.style.top = `${top}px`
    popover.style.left = `${left}px`

    popover.addEventListener('mouseenter', () => clearTimeout(this.hidePopoverTimeout))
    popover.addEventListener('mouseleave', () => this.scheduleHidePopover())
    popover.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-action]')
      if (!button) return

      event.preventDefault()
      event.stopPropagation()
      this.onHighlightAction?.(fieldPath, button.dataset.action, match)
    })

    overlay.appendChild(popover)
    this.popover = popover
  }

  scheduleHidePopover() {
    clearTimeout(this.hidePopoverTimeout)
    this.hidePopoverTimeout = setTimeout(() => {
      this.popover?.remove()
      this.popover = null
    }, 180)
  }

  /**
   * Remove all overlay highlights.
   */
  clear() {
    this.popover?.remove()
    this.popover = null
    this.overlays.forEach(overlay => overlay.remove())
    this.overlays.clear()
    this.highlights.clear()
  }

  /**
   * Get a page element by its index.
   */
  getPageElement(pageIndex) {
    const pages = this.viewerContainer.querySelectorAll('.page')
    return pages[pageIndex] || null
  }
}

/**
 * Compute union bounding rectangle relative to a container rect.
 */
function computeUnionRect(rects, containerRect) {
  if (rects.length === 0) return { left: 0, top: 0, width: 0, height: 0 }

  let minLeft = Infinity
  let minTop = Infinity
  let maxRight = -Infinity
  let maxBottom = -Infinity

  rects.forEach(r => {
    minLeft = Math.min(minLeft, r.left - containerRect.left)
    minTop = Math.min(minTop, r.top - containerRect.top)
    maxRight = Math.max(maxRight, r.right - containerRect.left)
    maxBottom = Math.max(maxBottom, r.bottom - containerRect.top)
  })

  return {
    left: minLeft - 2,
    top: minTop - 1,
    width: (maxRight - minLeft) + 4,
    height: (maxBottom - minTop) + 2
  }
}

function escapeHtml(value) {
  const div = document.createElement('div')
  div.textContent = value == null ? '' : String(value)
  return div.innerHTML
}

export default HighlightOverlay
